import { useMemo } from "react";
import { IOrderButtonLogic } from "../../components/order_entry/OrderStepButtonsFn/IOrderStepButtonsLogic";
import OrderSourceEnum from "../../models/OrderSourceEnum";
import OrderEntryService from "../../services/OrderEntryService";
import OrderStatesEnum from "../../models/OrderStatesEnum";
import ISupplierModel from "../../models/ISupplierModel";
import IUserUI from "../../models/IUserUI";
import IOrderUI from "../../models/IOrderUI";
import UserTypesEnum from "../../models/UserTypesEnum";

export const useOrderButtonLogic = (
    order: IOrderUI,
    supplierModel: ISupplierModel,
    userModel: IUserUI,
    hasEditMode: boolean,
    // Pass validation flags directly
    hasPendingDetailChanges?: boolean,
    hasPendingFineDetailChanges?: boolean,
    isFineDetailsValid?: boolean,
    hasRowsInEditMode?: boolean
    // Pass necessary callbacks or results if calculation depends on them
    // Example: showPickPackButtonsResult: boolean // If calculated outside
): IOrderButtonLogic => {
    const getButtonDetail = (order: IOrderUI, supplierModel: ISupplierModel): { btnClass: string; btnTitle: string } => {
        // Add real logic here, this is placeholder
        return { btnClass: "btn-primary", btnTitle: "submit_order" };
    };

    const logic = useMemo((): IOrderButtonLogic => {
        // Default state if essential data is missing
        const defaultLogic: IOrderButtonLogic = {
            isReady: false,
            isQuote: false,
            orderSteps: [],
            allowReturnToPreliminary: false,
            allowSubmit: false,
            allowRemake: false,
            allowReorder: false,
            currentStep: undefined,
            nextStep: undefined,
            submitBtnDisabledReason: null,
            stepButtonDisabled: false,
            stepButtonDisabledReason: null,
            creditLimitReason: null,
            buttonClassName: "",
            buttonTitle: "",
            isButtonDisabled: true,
            disabledLink: null,
            showResendEmailOrderBtn: false,
            showResendOrderAutomationBtn: false,
            showManualOrderStepChange: false,
            showOnHoldBtn: false,
            showCancelButton: false,
            showUncancelButton: false,
            showApproveDeclineButtons: false,
            hideMetaDataButton: true,
            orderIsOnHold: false,
            orderIsCancelled: false,
            showMarkBackorderBtn: false,
        };

        if (!order || !supplierModel) {
            return defaultLogic;
        }

        // --- Start Calculations (similar to JS version, but with types) ---
        const isQuote = order.is_quote || false;
        const orderIsCancelled = order.is_cancelled || false;
        const orderIsOnHold = order.is_on_hold || false;

        let hasShipments = supplierModel.has_shipments ?? false;
        if (order.order_source_id !== OrderSourceEnum.ORDERWERKS) {
            hasShipments = false;
        }

        // Assume services are typed correctly
        const effectiveSupplierModel = { ...supplierModel, has_shipments: hasShipments };
        const orderSteps = OrderEntryService.getOrderSteps(
            effectiveSupplierModel,
            OrderStatesEnum.SHIPPED, // Assuming target state
            order.Workflow?.detail
        );

        const { currentStep, nextStep } = OrderEntryService.getCurrentAndNextOrderSteps(order, supplierModel);

        const allowReturnToPreliminary = !!(
            OrderEntryService.isPreviousStepPreliminary(order, supplierModel) && supplierModel.allow_back_to_prelim
        );

        // --- Disability Checks ---
        let stepButtonDisabled = false;
        let stepButtonDisabledReason: string | null = null;
        let creditLimitReason: { id: string; availableBalance: number } | null = null; // Should the CreditLimitReason be an enum?

        if (hasPendingDetailChanges) {
            stepButtonDisabled = true;
            stepButtonDisabledReason = "pending_changes_save_first";
        } else if (hasPendingFineDetailChanges) {
            stepButtonDisabled = true;
            stepButtonDisabledReason = "pending_detail_changes_save_first";
        } else if (!isFineDetailsValid) {
            stepButtonDisabled = true;
            stepButtonDisabledReason = "incomplete_details_entered";
        } else if (hasRowsInEditMode) {
            stepButtonDisabled = true;
            stepButtonDisabledReason = "save_all_rows_in_edit_mode";
        }

        // Credit Limit (only if not already disabled)
        if (!stepButtonDisabled && order.Vendor && supplierModel.has_vendor_credit_limits && (order.Vendor.credit_limit ?? 0) > 0) {
            const creditLimit = order.Vendor.credit_limit ?? 0;
            const openBalance = order.Vendor.open_balance ?? 0;
            const availableBalance = creditLimit - openBalance;
            const grandTotal = order.grand_total ?? 0;

            if (availableBalance < grandTotal) {
                stepButtonDisabled = true;
                creditLimitReason = {
                    id: "not_enough_available_credit",
                    availableBalance: availableBalance,
                };
                stepButtonDisabledReason = creditLimitReason.id;
            }
        }

        // --- Button Visibility and Details ---
        let finalButtonClassName = "";
        let finalButtonTitle = "";
        // Assume base submit disability comes from outside or is always false initially
        const isSubmitBtnDisabledBase = false; // Placeholder
        const submitBtnDisabledReason = null; // Placeholder
        const disabledLink = null; // Placeholder

        const finalIsButtonDisabled = isSubmitBtnDisabledBase || stepButtonDisabled; // Combined disability

        if (isQuote) {
            finalButtonClassName = "btn-warning";
            finalButtonTitle = "convert_to_order";
        } else {
            const btnDetails = getButtonDetail(order, supplierModel); // Use helper
            finalButtonClassName = btnDetails.btnClass;
            finalButtonTitle = btnDetails.btnTitle;
        }

        // Visibility flags
        let showResendEmailOrderBtn = false;
        let showResendOrderAutomationBtn = false;
        let showManualOrderStepChange = false;
        const showOnHoldBtn = !isQuote && !!supplierModel.allow_orders_on_hold;
        let showCancelButton = false;
        let showUncancelButton = false;

        if (!isQuote) {
            if (orderIsCancelled) {
                showUncancelButton = true;
            } else {
                // Simplified logic from original - check states where cancel is NOT allowed
                const nonCancellableStates = [
                    OrderStatesEnum.PRELIMINARY,
                    OrderStatesEnum.SHIPPED,
                    OrderStatesEnum.PICKED_UP,
                    OrderStatesEnum.DELIVERED,
                ];
                if (order.order_state_id && !nonCancellableStates.includes(order.order_state_id)) {
                    showCancelButton = true;
                }

                if (!userModel.isSupplierUser) {
                    // Override if step is read-only
                    if (currentStep?.isVendorReadOnly) {
                        showCancelButton = false;
                    }
                }

                if (userModel.isSupplierUser) {
                    if (userModel.userType?.id === UserTypesEnum.ADMIN) {
                        if (currentStep?.isSupplierAdminReadOnly) {
                            showCancelButton = false;
                        }
                    } else {
                        if (currentStep?.isSupplierReadOnly) {
                            showCancelButton = false;
                        }
                    }
                }
            }
        }

        if (userModel?.isSupplierUser && userModel.userType?.id === UserTypesEnum.ADMIN) {
            const manualChangeSources: OrderSourceEnum[] = [
                OrderSourceEnum.APP,
                OrderSourceEnum.ORDERWERKS,
                OrderSourceEnum.OTHER,
                OrderSourceEnum.WAYFAIR,
            ];
            if (order.order_source_id && manualChangeSources.includes(order.order_source_id)) {
                showManualOrderStepChange = true;
            }
        }

        // This seems wrong to use a >= here..
        if (order.order_state_id && supplierModel.has_automation && order.order_state_id >= OrderStatesEnum.CONFIRMED) {
            showResendOrderAutomationBtn = true;
        }

        // This seems wrong to use a >= here..
        if (order.order_state_id && userModel?.isSupplierUser && order.order_state_id > OrderStatesEnum.SUBMITTED) {
            showResendEmailOrderBtn = true;
        }

        const showApproveDeclineButtons = !!(
            !isQuote &&
            supplierModel.has_approve_decline &&
            !order.approve_decline && // Only if not already approved/declined
            userModel?.isSupplierUser
        );

        const hideMetaDataButton = !!supplierModel.settings?.screens?.order_entry?.hide_meta_data;

        // Placeholder for features potentially calculated outside/passed in

        const allowSubmit = (): boolean => {
            if (order) {
                const result = OrderEntryService.allowSubmit(supplierModel, userModel, order, hasEditMode);
                return result.allowSubmit;
            } else {
                return false;
            }
        };

        const allowReorder = () => {
            let allow = false;

            if (supplierModel.has_reorders) {
                if (order?.order_state_id !== OrderStatesEnum.PRELIMINARY) {
                    allow = true;
                }

                if (order.is_quote) {
                    allow = true;
                }
            }

            return allow;
        };

        const allowRemake = () => {
            let allowRemake = false;

            if (supplierModel.has_remakes && userModel.isSupplierUser) {
                const orderSteps = OrderEntryService.getOrderSteps(supplierModel, OrderStatesEnum.SHIPPED);

                const currentStepOrderStateId = order!.order_state_id;

                const { currentStep, nextStep } = OrderEntryService.getCurrentAndNextOrderSteps(order, supplierModel);

                if (userModel.isSupplierUser) {
                    if (currentStep?.allowRemake) {
                        allowRemake = true;
                    }
                }

                // check if we're past the allow remake point
                let stepNumAllowRemake = 0;
                const currentStepNum = currentStep?.step_no || 0;

                for (const step of orderSteps) {
                    if (step.allowRemake) {
                        stepNumAllowRemake = step.step_no;
                    }
                }

                if (stepNumAllowRemake !== 0 && stepNumAllowRemake <= currentStepNum) {
                    allowRemake = true;
                }

                if (order.is_quote) {
                    allowRemake = true;
                }
            }

            if (!userModel.isSupplierUser) {
                if (supplierModel.hide_remake_from_accounts) {
                    allowRemake = false;
                }
            }

            return allowRemake;
        };

        const showMarkBackorderBtn = () => {
            if (supplierModel.has_backorders && userModel.isSupplierUser && userModel.userType?.id === UserTypesEnum.ADMIN) {
                return true;
            }

            return false;
        };

        // --- Return Derived State ---
        return {
            ...defaultLogic, // Start with defaults
            isReady: true,
            allowSubmit: allowSubmit(),
            allowReorder: allowReorder(),
            allowRemake: allowRemake(),
            isQuote,
            orderSteps,
            allowReturnToPreliminary,
            currentStep,
            nextStep,
            submitBtnDisabledReason,
            stepButtonDisabled,
            stepButtonDisabledReason,
            creditLimitReason,
            buttonClassName: finalButtonClassName,
            buttonTitle: finalButtonTitle,
            isButtonDisabled: finalIsButtonDisabled,
            disabledLink,
            showResendEmailOrderBtn,
            showResendOrderAutomationBtn,
            showManualOrderStepChange,
            showOnHoldBtn,
            showCancelButton,
            showUncancelButton,
            showApproveDeclineButtons,
            hideMetaDataButton,
            orderIsOnHold,
            orderIsCancelled,
            showMarkBackorderBtn: showMarkBackorderBtn(),
        };
    }, [
        order,
        supplierModel,
        userModel,
        hasPendingDetailChanges,
        hasPendingFineDetailChanges,
        isFineDetailsValid,
        hasRowsInEditMode,
        // Add other dependencies: showPickPackButtonsResult, etc.
    ]);

    return logic;
};
